import React, { useEffect, useState } from 'react';
import {
  Show,
  DateField,
  TopToolbar,
  ResourceContextProvider,
  ListButton,
  useRecordContext,
  useRedirect,
  ArrayField,
  Datagrid,
  TextField,
  BooleanField,
  FunctionField,

} from 'react-admin';
import { Button as MuiButton, Box, Breadcrumbs, Link, Typography, Chip, Tabs, Tab, Card, IconButton, Stack, } from '@mui/material';
import { useRefresh } from 'react-admin';
import MaterialCreateDialog from './MaterialCreateDialog';
import MaterialEditDialog from './MaterialEditDialog';
import { NavigateNext as NavigateNextIcon, Edit as EditIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

type Material = {
  id?: string;
  name?: string;
  description?: string | null;
  unit?: string;
  cost?: number | null;
  isActive?: boolean;
};

const ConfigurationBreadcrumbs = () => {
  const record = useRecordContext();
  const redirect = useRedirect();

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      sx={{ mb: 3 }}
    >
      <Link
        underline="hover"
        component="button"
        variant="body1"
        onClick={() => redirect('/configuration')}
        sx={{ cursor: 'pointer' }}
      >
        Configuration
      </Link>
      <Link
        underline="hover"
        component="button"
        variant="body1"
        onClick={() => redirect('/configuration/material-categories')}
        sx={{ cursor: 'pointer' }}
      >
        Material Categories
      </Link>
      <Typography color="text.primary">{record?.name || 'Material Category'}</Typography>
    </Breadcrumbs>
  );
};

export const MaterialCategoryShow = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    return tab ? parseInt(tab, 10) : 0;
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    setTabValue(tab ? parseInt(tab, 10) : 0);
  }, [location.search]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const params = new URLSearchParams(location.search);
    params.set('tab', newValue.toString());
    navigate(`?${params.toString()}`, { replace: true });
  };


  const TabPanel = ({ children, value, index }: { children: React.ReactNode, value: number, index: number }) => {
    return (
      <Box hidden={value !== index} sx={{ pt: 2 }}>
        {value === index && children}
      </Box>
    );
  };

  const MaterialCategoryDetails: React.FC = () => {
    const record = useRecordContext();
    const refresh = useRefresh();
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editingMaterialId, setEditingMaterialId] = useState<string>('');

    return (
      <Box sx={{ p: 0 }}>
        <TabPanel value={tabValue} index={0}>
          <Card sx={{ p: 2, position: 'relative' }}>
            <IconButton
              onClick={() => {
                navigate(`/configuration/material-categories/${record.id}/edit`);
              }}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'action.hover',
                '&:hover': {
                  bgcolor: 'action.selected',
                }
              }}
              title="Edit Basic Info"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1">{record?.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body1">{record?.description || 'No description'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <br />
                <Chip
                  label={record?.isActive ? 'Active' : 'Inactive'}
                  color={record?.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Updated At</Typography>
                <br />
                <DateField
                  record={record}
                  source="updatedAt"
                  showTime
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created At</Typography>
                <br />
                <DateField
                  record={record}
                  source="createdAt"
                  showTime
                />
              </Box>
            </Stack>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Render inline table from record.materials (no separate resource call) */}
          {record?.materials && record.materials.length > 0 ? (
            <ArrayField source="materials" record={record}>
              <Datagrid
                bulkActionButtons={false}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  '& .RaDatagrid-headerCell': { backgroundColor: 'grey.100' },
                  '& .column-actions': { width: 100 }, // Add fixed width for actions column
                }}
              >
                <TextField source="name" label="Name" />
                <TextField source="description" label="Description" />
                <TextField source="unit" label="Unit" />
                <FunctionField
                  label="Cost"
                  render={(rec: Material) => {
                    const num = rec?.cost ?? 0
                    const out = Number.isFinite(num) ? num.toFixed(2) : (num ?? '-');
                    return <span style={{ textAlign: 'right' }}>{out}</span>;
                  }}
                />
                <BooleanField source="isActive" label="Active" />
                  <FunctionField
                    label="Actions"
                    render={(rec: Material) => (
                      <IconButton
                        onClick={() => {
                          setEditingMaterialId(String(rec.id || ''));
                          setEditOpen(true);
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  />
              </Datagrid>
            </ArrayField>
          ) : (
            <Typography color="text.secondary" sx={{ mt: 1 }}>No materials found for this category.</Typography>
          )}
          <div style={{ padding: '10px 0' }} />
          <MuiButton
            variant="outlined"
            onClick={() => setCreateOpen(true)}
            size="small"
            sx={{ mb: 2 }}
          >
            Create Material
          </MuiButton>

          <MaterialCreateDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            categoryId={String(record?.id || '')}
            onSuccess={() => {
              setCreateOpen(false);
              // Refresh the show view to pick up the new material
              refresh();
            }}
          />

          <MaterialEditDialog
            open={editOpen}
            onClose={() => {
              setEditOpen(false);
              setEditingMaterialId('');
            }}
            materialId={editingMaterialId}
            onSuccess={() => {
              setEditOpen(false);
              setEditingMaterialId('');
              refresh();
            }}
          />

        </TabPanel>
      </Box>
    );
  };

  return (
    <ResourceContextProvider value="materials/categories">
      <Show
        title="Workflow Category Details"
        actions={<MaterialCategoryShowActions />}
      >
        <Box sx={{ p: 2 }}>
          <ConfigurationBreadcrumbs />

          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Basic Info" />
            <Tab label="Materials" />
          </Tabs>

          <MaterialCategoryDetails />
        </Box>
      </Show>
    </ResourceContextProvider>
  );
};

const MaterialCategoryShowActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
);
